<template>
  <div>
    <b-spinner v-show="!ready" label="Initializing"></b-spinner>

    <input
      type="hidden"
      :name="$attrs.name"
      :value="$_vModel"
      :amplitude-attribute="JSON.stringify(amplitudeAttribute)"
    />

    <custom-quill-editor
      v-show="ready"
      v-model="$_vModel"
      v-bind="{ ...$attrs }"
      :options="$_editorOptions"
      v-on="{ ...$listeners, change: onEditorChange }"
      @input.native="onEditorInput"
      @ready="onEditorReady"
    ></custom-quill-editor>
  </div>
</template>

<script>
import { amplitudeMixin } from '@/mixins/amplitudeMixin';
import Quill from 'quill';

const Embed = Quill.import('blots/embed');

// Custom LineBreak blot for Shift+Enter
class LineBreak extends Embed {
  static blotName = 'linebreak';
  static tagName = 'BR';
  
  static create() {
    const node = super.create();
    return node;
  }
  
  length() {
    return 1;
  }
  
  insertInto(parent, ref) {
    // Ensure proper insertion into the document
    super.insertInto(parent, ref);
  }
}

Quill.register(LineBreak);

export default {
  mixins: [amplitudeMixin],

  model: {
    prop: 'vModel',
    event: 'change'
  },

  props: {
    formats: {
      type: Array,
      default: () => [
        // "background",
        'bold',
        // "color",
        // "font",
        'code',
        'italic',
        'link',
        // "size",
        // "strike",
        'script',
        'underline',
        // "blockquote",
        'header',
        'image',
        'imagewithstyle',
        'indent',
        'list',
        'align',
        'width',
        'style',
        // "direction",
        // "code-block",
        'formula',
        // 'video'
        'linebreak'
      ]
    },

    // modules: {
    //   type: Object,
    //   default: () => ({
    //     toolbar: [
    //       [{ list: "ordered" }, { list: "bullet" }],
    //       [
    //         {
    //           link: function(value) {
    //             console.log("Link handler:", value);

    //             if (value) {
    //               var href = prompt("Enter the URL");
    //               this.quill.format("link", href);
    //             } else {
    //               this.quill.format("link", false);
    //             }
    //           },
    //         },
    //       ],
    //     ],
    //   }),
    // },

    toolbar: {
      type: Array,
      default: () => [
        ['bold', 'italic', 'underline'],
        // ["code-block"],
        ['formula'],
        [{ header: '1' }, { header: '2' }],
        [{ list: 'ordered' }, { list: 'bullet' }],
        [{ script: 'sub' }, { script: 'super' }],
        [{ indent: '-1' }, { indent: '+1' }],
        [{ align: [] }],
        ['link'],
        ['image']
      ]
    },

    showToolbar: {
      type: Boolean,
      default: true
    },

    placeholder: {
      type: String,
      default: ''
    },

    editorOptions: {
      type: Object,
      default: () => ({})
    },

    dropImage: {
      type: Boolean,
      default: true
    },

    vModel: {
      type: String,
      default: ''
    },

    amplitudeDebounceDelay: {
      type: Object,
      default: () => ({
        onClick: null,
        onSubmit: null,
        onChange: 3000,
        onFileUploaded: null,
        onFileRemoved: null
      })
    }
  },

  data() {
    return {
      text: this.vModel || null,
      ready: false
    };
  },

  computed: {
    $_vModel: {
      get() {
        return this.vModel;
      },

      set(v) {
        this.$emit('change', v);
      }
    },

    $_editorOptions() {
      const {
        formats,
        toolbar: propsToolbar,
        showToolbar,
        placeholder,
        editorOptions,
        dropImage
      } = this;

      let toolbar = [];
      let imageResize = false;
      let imageDrop = false;

      if (showToolbar) {
        toolbar = propsToolbar;
      }

      if (dropImage) {
        imageResize = {
          modules: ['Resize', 'DisplaySize']
        };
        imageDrop = dropImage;
      }

      return {
        ...editorOptions,
        formats: [...formats],
        modules: {
          toolbar: toolbar.length ? [...toolbar] : false,
          clipboard: {
            allowed: {
              tags: ['a', 'b', 'strong', 'u', 's', 'i', 'p', 'br', 'ul', 'ol', 'li', 'span'],
              attributes: ['href', 'rel', 'target', 'class']
            },
            // keepSelection: true,
            // substituteBlockElements: true,
            magicPasteLinks: true,
            matchers: [
              // Ensure BR tags are preserved as linebreak blots
              ['BR', function() {
                const newDelta = new (Quill.import('delta'))();
                newDelta.insert({ linebreak: true });
                return newDelta;
              }]
            ]
            // hooks: {
            //   // beforeSanitizeAttributes(node) {
            //   //   // console.log(node, node.nodeName);
            //   //   // console.log(node.parentNode.nodeName, node.nodeName);
            //   //   if (node)
            //   //     if (node.nodeName === 'LI') {
            //   //       console.log(node, node.textContent);
            //   //     }
            //   // }
            //   // uponSanitizeElement(node, data) {
            //   //   console.log(node, data);
            //   // },
            // }
          },
          keyboard: {
            bindings: {
              tab: {
                key: 9,
                handler: function (range) {
                  this.quill.formatLine(range, 'indent', '+1'); //handling when calling tab on normal paragraph, the class `ql-indent-#{x}` has not been included.
                }
              },
              shiftEnter: {
                key: 13,
                shiftKey: true,
                handler: function(range) {
                  // Insert a <br> tag instead of a new paragraph
                  this.quill.deleteText(range.index, range.length);
                  this.quill.insertEmbed(range.index, 'linebreak', true, 'user');

                  const [line, offset] = this.quill.getLine(range.index + 1);
                  // In Quill, line.length() includes the trailing block-separating newline (\n).
                  // If the offset after our insertion is at the very end of the line (just before \n),
                  // we add an extra padding <br> to force the browser to render the new line.
                  if (offset === line.length() - 1) {
                    this.quill.insertEmbed(range.index + 1, 'linebreak', true, 'user');
                    this.quill.setSelection(range.index + 1, Quill.sources.USER);
                  } else {
                    this.quill.setSelection(range.index + 1, Quill.sources.USER);
                  }
                  return false; // Prevent default behavior
                }
              }
            }
          },
          imageResize: imageResize,
          imageDrop
        },
        placeholder
      };
    }
  },
  mounted() {
    const target = this.$el.querySelector('div.ql-editor');

    target.addEventListener('paste', (event) => {
      event.preventDefault();
      let { innerHTML } = target;

      let prop = {
        Id: this.$attrs.id,
        Name: this.$attrs.name,
        Type: 'texteditor',
        Value: innerHTML,
        ...this.amplitudeAttribute
      };
      this.$_amplitudeMixin_onChange(target, prop);
    });
  },

  methods: {
    onEditorChange(event) {
      const { html } = event;
      const formattedHtml = html.replaceAll(/href="(.*?)"/g, function (matchHref) {
        if (matchHref.indexOf('http') === -1) {
          matchHref = matchHref.replace('href="', 'href="https://');
        }

        return matchHref;
      });
      this.$_vModel = formattedHtml;
    },

    onEditorInput(event) {
      // console.log(event);
      const { innerHTML } = event.target;
      this.$_amplitudeMixin_onChange(event.target, {
        Id: this.$attrs.id,
        Name: this.$attrs.name,
        Type: 'texteditor',
        Value: innerHTML,
        ...this.amplitudeAttribute
      });
    },

    onEditorReady() {
      this.ready = true;
    }
  }
};
</script>

<style lang="scss">
.quill-editor {
  .ql-editor {
    min-height: 10rem;

    &.ql-blank {
      &::before {
        font-family: Heebo;
        font-style: normal;
        font-weight: normal;
        font-size: 1rem;
      }
    }

    p {
      margin-bottom: 1rem;
    }
  }
  .ql-container.ql-snow.ql-disabled {
    border: none !important;
  }

  .ql-editor[contenteditable='false'] {
    background-color: #eeeff8 !important;
    border: 1px solid #d1d2db;
    border-radius: 0.29rem;
  }
}
</style>
